import { useCallback, useEffect, useState } from "react";
import { Info } from "lucide-react";
import { fetchEmailTemplateById, previewEmailTemplate } from "../api/adminClient";
import { templateDisplay } from "./templateLabel";

/** Read-only look at a template exactly as a recipient would receive it.
 *  Rendered inline in the main column (not as an overlay) so the reminder
 *  schedule stays visible alongside it — an admin can click through each
 *  step's emails without a dialog covering the steps they're comparing.
 *  Preview HTML comes from the server through the same path a real send
 *  uses, so this is a true preview rather than a client-side approximation. */
export default function TemplatePreviewPanel({ templateId, audienceLabel, onClose, onEdit }) {
  const [template, setTemplate] = useState(null);
  const [html, setHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [width, setWidth] = useState("desktop");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const tplRes = await fetchEmailTemplateById(templateId);
      const tpl = tplRes.data?.template;
      setTemplate(tpl);
      const preview = await previewEmailTemplate({
        subject: tpl.subject,
        content_html: tpl.content_html,
      });
      setSubject(preview.data?.subject || tpl.subject || "");
      setHtml(preview.data?.html || "");
    } catch (err) {
      setError(err.message || "Could not load this template.");
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    load();
  }, [load]);

  const display = template ? templateDisplay(template, audienceLabel) : null;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-start justify-between gap-4 px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#004f73]/70">
            Template preview
          </p>
          <h2 className="text-base font-semibold text-gray-900 truncate leading-6 mt-0.5">
            {display?.title || "Loading…"}
          </h2>
          {display ? (
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <span className={`text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full ${display.typeClass}`}>
                {display.typeLabel}
              </span>
              {display.audienceShort ? (
                <span
                  className="text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-white text-slate-600 border border-slate-200"
                  title={display.audienceFull}
                >
                  {display.audienceShort}
                </span>
              ) : null}
              {display.timing ? (
                <span className="text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-white text-slate-500 border border-slate-200">
                  {display.timing}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-0.5 bg-white">
            <button
              type="button"
              className={`px-2 py-1 text-xs font-medium rounded ${
                width === "mobile" ? "bg-slate-100 text-[#004f73]" : "text-slate-500"
              }`}
              onClick={() => setWidth("mobile")}
            >
              Mobile
            </button>
            <button
              type="button"
              className={`px-2 py-1 text-xs font-medium rounded ${
                width === "desktop" ? "bg-slate-100 text-[#004f73]" : "text-slate-500"
              }`}
              onClick={() => setWidth("desktop")}
            >
              Desktop
            </button>
          </div>
          {onEdit && template ? (
            <button
              type="button"
              className="px-3 py-1.5 text-sm font-medium text-[#004f73] border border-[#004f73]/30 rounded-lg hover:bg-[#004f73]/5 bg-white"
              onClick={() => onEdit(template.id)}
            >
              Edit
            </button>
          ) : null}
          <button
            type="button"
            aria-label="Close preview"
            title="Close preview"
            className="h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-200"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#004f73]">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 py-10 text-center">{error}</p>
        ) : (
          <>
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-slate-500 mb-0.5">Subject</p>
                <p className="text-sm font-semibold text-gray-900 break-words">{subject}</p>
              </div>
              <span
                className="shrink-0 mt-0.5 text-slate-400"
                title="Sample data. Real sends use each recipient's details."
              >
                <Info size={14} />
              </span>
            </div>
            <div
              className={`border border-slate-200 rounded-xl overflow-hidden bg-slate-50 ${
                width === "mobile" ? "max-w-sm mx-auto" : "w-full"
              }`}
            >
              <iframe
                title="Template preview"
                sandbox=""
                srcDoc={html}
                className="w-full bg-white"
                style={{ height: 560, border: 0 }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
